import clsx from "clsx";
import type { ReactNode } from "react";
import { fmtNumber } from "../format";

/** Tabs with a brand underline. Counts render after a middle dot ("Access · 9"). Arrow keys move between tabs. */
export function Tabs<T extends string>({ tabs, value, onChange, ariaLabel, className }: { tabs: { key: T; label: ReactNode; count?: number }[]; value: T; onChange: (t: T) => void; ariaLabel?: string; className?: string }) {
  const onKey = (e: React.KeyboardEvent, i: number) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const next = tabs[(i + (e.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length]!;
    onChange(next.key);
    (e.currentTarget.parentElement?.children[tabs.indexOf(next)] as HTMLElement | undefined)?.focus();
  };
  return (
    <div className={clsx("flex gap-1 overflow-x-auto border-b border-line", className)} role="tablist" aria-label={ariaLabel}>
      {tabs.map((t, i) => {
        const on = value === t.key;
        return (
          <button key={t.key} type="button" role="tab" aria-selected={on} tabIndex={on ? 0 : -1} onClick={() => onChange(t.key)} onKeyDown={(e) => onKey(e, i)}
            className={clsx("-mb-px whitespace-nowrap border-b-2 px-3.5 py-2.5 transition-colors", on ? "border-brand font-semibold text-fg" : "border-transparent text-fg-2 hover:text-fg")}>
            {t.label}{t.count !== undefined && <span className={clsx("font-medium", on ? "text-fg-2" : "text-fg-3")}> · {fmtNumber(t.count)}</span>}
          </button>
        );
      })}
    </div>
  );
}
